import React, { FC, Fragment, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/effect-fade';
import type { Swiper as SwiperClass } from 'swiper/types';
import classes from './StartWithSetting.module.css';

interface ProductCardTracking {
  settingId?: string;
  price?: number | string;
  ringStyle?: string;
  diamondShape?: string;
  centerDiamondType?: string;
  color?: string;
  imageUrl?: string;
  payload: string;
}

interface ProductCardProps {
  product: any;
  uniqueProductId: string;
  isSelected: boolean;
  isStuds: boolean;
  showImageSlider: boolean;
  selectedImage?: { imagePath?: string; sideImagePath?: string; swatch?: string };
  swatches: { [key: string]: string };
  swatchLoadingIds: Set<string>;
  loadedImages: Set<string>;
  href: string;
  tracking: ProductCardTracking;
  onImageLoad: (src?: string) => void;
  onSelect: () => void;
  onSwatchClick: (key: string) => void;
}

const ProductCard: FC<ProductCardProps> = ({
                                             product,
                                             uniqueProductId,
                                             isSelected,
                                             isStuds,
                                             showImageSlider,
                                             selectedImage,
                                             swatches,
                                             swatchLoadingIds,
                                             loadedImages,
                                             href,
                                             tracking,
                                             onImageLoad,
                                             onSelect,
                                             onSwatchClick,
                                           }) => {
  const swiperRef = useRef<SwiperClass | null>(null);
  const isLoadingSwatch = swatchLoadingIds.has(uniqueProductId);
  const SLIDE_TRANSITION_MS = 300;

  return (
    <a
      id={isSelected ? "scrollToTop" : ""}
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        onSelect();
      }}
      onMouseEnter={() => {
        if (!showImageSlider && !isStuds) swiperRef.current?.slideNext(SLIDE_TRANSITION_MS);
      }}
      onMouseLeave={() => {
        if (!showImageSlider && !isStuds) swiperRef.current?.slideTo(0, SLIDE_TRANSITION_MS);
      }}
      className={`${classes.collection} rb-track-setting-tile`}
      data-rb-setting-id={tracking.settingId}
      data-rb-price={tracking.price}
      data-rb-ring-style={tracking.ringStyle}
      data-rb-diamond-shape={tracking.diamondShape}
      data-rb-center-diamond-type={tracking.centerDiamondType}
      data-rb-color={tracking.color}
      data-rb-image-url={tracking.imageUrl}
      data-rb-payload={tracking.payload}
    >
      <div className={classes.collection_item}>
        <div className={classes.brandTitle}>
          {product.design_origin !== "Generic"
            ? product.design_origin === "Reve"
              ? "rêve"
              : product.design_origin
            : null}{" "}
        </div>
        <div
          className={classes.collection_product}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest('.swiper-scrollbar, [class*="cardPaginationWrap"]')) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          <div
            className={classes.collection_image}
          >
            {isStuds ? (
              <img
                src={selectedImage?.imagePath}
                alt={product?.Name}
                className={`${classes.image} ${loadedImages.has(selectedImage?.imagePath || "") ? classes.imageLoaded : ""} ${isLoadingSwatch ? classes.imageHidden : ""}`}
                loading={"lazy"}
                onLoad={() => onImageLoad(selectedImage?.imagePath)}
              />
            ) : (
              <Swiper
                key={showImageSlider ? 'touch' : 'desktop'}
                className={classes.imageSwiper}
                observer={true}
                observeParents={true}
                speed={SLIDE_TRANSITION_MS}
                effect={showImageSlider ? 'slide' : 'fade'}
                fadeEffect={{ crossFade: true }}
                scrollbar={{
                  hide:          false,
                  draggable:     false,
                  snapOnRelease: true,
                  el:            `#pagination-${uniqueProductId}`,
                }}
                modules={[ Scrollbar, EffectFade ]}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
              >
                <SwiperSlide>
                  <img
                    src={selectedImage?.imagePath}
                    alt={product?.Name}
                    className={`${classes.image} ${loadedImages.has(selectedImage?.imagePath || "") ? classes.imageLoaded : ""} ${isLoadingSwatch ? classes.imageHidden : ""}`}
                    loading={"lazy"}
                    onLoad={() => {
                      onImageLoad(selectedImage?.imagePath);
                      swiperRef.current?.update();
                    }}
                  />
                </SwiperSlide>
                <SwiperSlide>
                  <img
                    src={selectedImage?.sideImagePath}
                    alt={`${product?.Name} side view`}
                    className={`${classes.image} ${loadedImages.has(selectedImage?.sideImagePath || "") ? classes.imageLoaded : ""} ${isLoadingSwatch ? classes.imageHidden : ""}`}
                    loading={"lazy"}
                    onLoad={() => {
                      onImageLoad(selectedImage?.sideImagePath);
                      swiperRef.current?.update();
                    }}
                  />
                </SwiperSlide>
              </Swiper>
            )}
          </div>
          {/*{!showImageSlider && !isStuds && (
            <>
              <button
                type="button"
                className={`${classes.cardArrow} ${classes.cardArrowPrev}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const swiper = swiperRef.current;
                  if (!swiper) return;
                  const total = swiper.slides.length;
                  swiper.slideTo((swiper.activeIndex - 1 + total) % total);
                }}
                aria-label="Previous"
              >
                <Icon name="hk_chevron_left"/>
              </button>
              <button
                type="button"
                className={`${classes.cardArrow} ${classes.cardArrowNext}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const swiper = swiperRef.current;
                  if (!swiper) return;
                  const total = swiper.slides.length;
                  swiper.slideTo((swiper.activeIndex + 1) % total);
                }}
                aria-label="Next"
              >
                <Icon name="hk_chevron_right"/>
              </button>
            </>
          )}*/}
          <div className={classes.content}>
            <ul className={classes.swatches}>
              {product.swatches && (
                <>
                  {Object.entries(product.swatches).map(([ key, value ]: any, swatchIndex: number) => {
                    return (
                      <Fragment key={`${uniqueProductId}.${swatchIndex}`}>
                        {value ? (
                          <li
                            tabIndex={0}
                            className={`${classes.swatches_item} ${
                              selectedImage?.swatch === key ? classes.selected : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              onSwatchClick(key)
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === "Spacebar" || e.key === " ") {
                                onSwatchClick(key)
                                e.preventDefault()
                              }
                            }}
                            title={swatches[key]}
                          >
                            <img
                              key={swatchIndex}
                              src={swatches[key] || "/placeholder.svg"}
                              alt={`Swatch ${swatchIndex + 1}`}
                            />
                          </li>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </>
              )}
            </ul>
            <h3 className={classes.title}>{product.Name} {isStuds ? 'Stud Earrings' : 'Setting'}</h3>
            <p className={classes.desc}>
              <span>Starting at </span>
              <span className={classes.price}>
            {product.price_at ? `$${product.price_at.toLocaleString("en-US")}` : "-"}{" "}
          </span>
            </p>
            {!isStuds && showImageSlider && (
              <div className={classes.cardPaginationWrap}>
                <div
                  id={`pagination-${uniqueProductId}`}
                  className={classes.cardPagination}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    const swiper = swiperRef.current;
                    if (!swiper) return;
                    const rect = event.currentTarget.getBoundingClientRect();
                    const clickRatio = (event.clientX - rect.left) / rect.width;
                    const totalSlides = swiper.slides.length;
                    const targetIndex = Math.min(
                      totalSlides - 1,
                      Math.max(0, Math.round(clickRatio * (totalSlides - 1)))
                    );
                    swiper.slideTo(targetIndex, SLIDE_TRANSITION_MS);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

export default ProductCard;
